import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service, Timeframe } from 'webapp-types';

import ForecastsSettingsLinearChangeInputComponent, {
	LinearChangePattern,
} from 'webapp/components/forecasts/settings/linear-change/input';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

export default class ForecastsPopupLinearChangeComponent extends PopupInnerModalComponent<PopupData.Forecast.LinearChange> {
	@service('account') declare accountService: Service.Account;

	@tracked value: number | null = null;
	@tracked changePattern: LinearChangePattern = LinearChangePattern.MONTHLY_PERCENT;
	@tracked timeframe: Timeframe | null;
	@tracked endDate: string | null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const data = this.popupData;
		this.value = data.value;
		this.changePattern = data.changePattern;
		this.timeframe = data.timeframe;
		this.endDate = data.endDate;
	}

	@action
	updateValue(value: number | null): void {
		this.value = value;
	}

	@action
	updateChangePattern(changePattern: LinearChangePattern): void {
		this.changePattern = changePattern;
	}

	@action
	apply(): void {
		const data = this.popupData;
		if (data.onApply) {
			data.onApply(this.value, this.changePattern);
		}
		this.close();
	}

	@action
	clear(): void {
		this.value = null;
		this.changePattern = LinearChangePattern.MONTHLY_PERCENT;
	}
	<template>
		<div class="content popup-linear-change">
			<div class="popup-linear-change__input-section">
				<ForecastsSettingsLinearChangeInputComponent
					@value={{this.value}}
					@changePattern={{this.changePattern}}
					@onChangeValue={{this.updateValue}}
					@onChangePattern={{this.updateChangePattern}}
					@timeframe={{this.timeframe}}
					@endDate={{this.endDate}}
					@skipDebounce={{true}}
				/>
			</div>

			<div class="popup-linear-change__description">
				<p>
					Simple linear increase/decrease is calculated based on chosen target setup.
					Future values won't update automatically when impacting metrics are added or
					changed.

					{{#unless this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins}}
						<a
							href="https://help.databox.com/databox-forecasting-methodology"
							class="learn-more-link"
							target="_blank"
						>Learn more
						</a>
					{{/unless}}
				</p>
			</div>
		</div>

		<div class="popup-linear-change__actions">
			<a role="button" class="cancel" {{on "click" this.clear}}>
				Clear
			</a>
			<SubmitButtonComponent @text="Apply" @extraClass="save" @onTrigger={{this.apply}} />
		</div>
	</template>
}
